// src/components/forms/pending-orders/PendingOrdersPackageInfo.tsx
// Componente para la sección de Información del Paquete

import React from 'react'
import type { PendingOrderFormData, OrderDetail, TipoServicioLiquidacion } from '../../../types/pendingOrdersFormTypes'
import { Input, Select } from '../../ui'
import { FormField } from '../../ui/Form'
import SectionTitle from '../../ui/SectionTitle'
import { CubeIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

interface PendingOrdersPackageInfoProps {
  formData: PendingOrderFormData
  orderDetail?: OrderDetail | null
  isExpanded: boolean
  isLiquidateMode: boolean
  hidePackageFields: string[]
  selectedTipoServicio: string
  tiposDeServicioLiquidacion: TipoServicioLiquidacion[]
  metodoPago: Array<{ value: string; label: string }>
  totalconFlete: string
  onToggleExpanded: () => void
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
  onTipoServicioChange: (value: string) => void
}

export const PendingOrdersPackageInfo: React.FC<PendingOrdersPackageInfoProps> = ({
  formData,
  orderDetail,
  isExpanded,
  isLiquidateMode,
  hidePackageFields,
  selectedTipoServicio,
  tiposDeServicioLiquidacion,
  metodoPago,
  totalconFlete,
  onToggleExpanded,
  onInputChange,
  onTipoServicioChange
}) => {
  // Convertir tipos de servicio a formato Select
  const tipoServicioOptions = [
    { value: '', label: 'Seleccione un tipo de servicio' },
    ...tiposDeServicioLiquidacion.map((tipo) => ({
      value: tipo.value,
      label: tipo.label
    }))
  ]

  // Convertir métodos de pago a formato Select
  const metodoPagoOptions = [
    { value: '', label: 'Seleccione método de pago' },
    ...metodoPago.map((forma) => ({
      value: forma.value,
      label: forma.label
    }))
  ]

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div 
        className="flex justify-between items-center cursor-pointer"
        onClick={onToggleExpanded}
      >
        <SectionTitle icon={CubeIcon} noMargin>Información del paquete</SectionTitle>
        <ChevronDownIcon className={`w-6 h-6 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
      </div>
      
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 transition-all duration-200 ${isExpanded ? 'mt-4' : 'hidden'}`}>
        {!hidePackageFields.includes('largo') && (
          <FormField
            label="Largo (CM)"
            required
          >
            <Input
              type="number"
              name="largo"
              value={Math.round(formData.largo)}
              onChange={onInputChange}
              min="0"
              step="0.1"
              mode="edit"
            />
          </FormField>
        )}
        {!hidePackageFields.includes('ancho') && (
          <FormField
            label="Ancho (CM)"
            required
          >
            <Input
              type="number"
              name="ancho"
              value={Math.round(formData.ancho)}
              onChange={onInputChange}
              min="0"
              step="0.1"
              mode="edit"
            />
          </FormField>
        )}
        {!hidePackageFields.includes('altura') && (
          <FormField
            label="Altura (CM)"
            required
          >
            <Input
              type="number"
              name="altura"
              value={Math.round(formData.altura)}
              onChange={onInputChange}
              min="0"
              step="0.1"
              mode="edit"
            />
          </FormField>
        )}
        {!hidePackageFields.includes('peso') && (
          <FormField
            label="Peso (KG)"
            required
          >
            <Input
              type="number"
              name="peso"
              value={formData.peso}
              onChange={onInputChange}
              min="0"
              step="0.1"
              mode="edit"
            />
          </FormField>
        )}
        {!hidePackageFields.includes('valor_declarado') && (
          <FormField
            label="Valor declarado (COP)"
            required
          >
            <Input
              type="number"
              name="valorDeclarado"
              value={Math.round(formData.valorDeclarado || 0)}
              onChange={onInputChange}
              min="0"
              readOnly={!isLiquidateMode}
              mode={isLiquidateMode ? 'edit' : 'view'}
            />
          </FormField>
        )}
        {!hidePackageFields.includes('valor_recaudar') && (
          <FormField
            label="Valor a Recaudar"
            required
          >
            <Input
              type="text"
              name="valorproducto"
              value={totalconFlete.toLocaleString()}
              readOnly={!isLiquidateMode}
              mode={isLiquidateMode ? 'edit' : 'view'}
            />
          </FormField>
        )}
        {!hidePackageFields.includes('metodo_pago') && (
          <FormField
            label="Método de Pago"
            required
          >
            <Select
              name="metodoPago"
              value={formData.metodoPago}
              onChange={onInputChange}
              disabled={!isLiquidateMode}
              options={metodoPagoOptions}
              placeholder="Seleccione método de pago"
              mode={isLiquidateMode ? 'edit' : 'view'}
            />
          </FormField>
        )}
        {!hidePackageFields.includes('id_transaccion') && (
          <FormField
            label="ID Transacción"
          >
            <Input
              type="text"
              value={orderDetail?.transaction_id || orderDetail?.extraDetails?.transaction_id || 'No asignado'}
              readOnly
              mode="view"
              placeholder="No asignado"
            />
          </FormField>
        )}
        {!hidePackageFields.includes('transportadora') && (
          <FormField label="Transportadora">
            <Input
              type="text"
              value={orderDetail?.extraDetails?.transportadora || 'No asignada'}
              readOnly
              mode="view"
            />
          </FormField>
        )}
        {!hidePackageFields.includes('tipo_servicio') && (
          <FormField
            label="Tipo de Servicio"
            required
          >
            <Select
              value={selectedTipoServicio}
              onChange={(e) => onTipoServicioChange(e.target.value)}
              disabled
              options={tipoServicioOptions}
              placeholder="Seleccione un tipo de servicio"
              mode="view"
            />
            <p className="mt-1 text-sm text-gray-500">
              {selectedTipoServicio === '12' 
                ? 'Paquete Terrestre (1-8 Kg, una unidad)' 
                : 'Mercancía Terrestre (más de 8 Kg o múltiples unidades)'}
            </p>
          </FormField>
        )}
        <div className="hidden">
          <FormField label="Recaudo contra entrega (COP)">
            <Input
              type="number"
              name="recaudoContraEntrega"
              value={formData.recaudoContraEntrega}
              onChange={onInputChange}
              min="0"
              mode="edit"
            />
          </FormField>
        </div>
      </div>
    </div>
  )
}

